import _kebabCase from 'lodash/kebabCase';

const COMPONENT_ATTR = 'data-component';
const COMPONENT_PROPS_ATTR = 'data-component-props';
const DIRECTIVE_ATTR = 'data-directive';

function getMatchedElements(el, selector) {
  const children = el.querySelectorAll(selector);
  return el.matches(selector) ? [el, ...children] : children;
}

class App {
  constructor(options = {}) {
    const {
      el,
      components,
      directives,
      methods,
    } = options;

    this.isReady = false;
    this.readyCallbacks = [];
    this.$options = {
      el,
      components: components || {},
      directives: directives || {},
    };
    this.$observer = null;
    this.$componentsInstances = new WeakMap();
    this.$boundDirectives = new WeakMap();

    if (methods) {
      Object.keys(methods).forEach((key) => {
        this[key] = methods[key].bind(this);
      });
    }

    if (['complete', 'loaded', 'interactive'].includes(document.readyState)) {
      this.$init();
    } else {
      document.addEventListener('DOMContentLoaded', this.$init.bind(this));
    }
  }

  $ready(cb) {
    if (this.isReady) {
      cb(this);
      return;
    }

    this.readyCallbacks.push(cb);
  }

  $init() {
    this.$el = (this.$options.el && document.querySelector(this.$options.el)) || document.body;

    if (!this.$el) {
      console.warn('App init failed - root element is not found');
      return;
    }

    this.$initComponents(this.$el);
    this.$bindDirectives(this.$el);
    this.$initObserver();
    this.isReady = true;

    this.readyCallbacks.forEach((cb) => cb(this));
    this.readyCallbacks = [];
  }

  $initComponents(el) {
    const elements = getMatchedElements(el, `[${COMPONENT_ATTR}]`);

    elements.forEach(async (el) => {
      const key = el.getAttribute(COMPONENT_ATTR);
      if (!key) return;

      // Prevent initialization many components on the same DOM element
      const existingInstance = this.$componentsInstances.get(el);
      if (existingInstance) {
        console.error('Cannot initialize two components on the same element', el);
        return;
      }

      let Component = this.$options.components[key];

      if (!Component) {
        console.warn(`Component with name "${key}" is not registered.`, el);
        return;
      }

      let props = el.getAttribute(COMPONENT_PROPS_ATTR);

      if (props) {
        try {
          props = JSON.parse(props);
        } catch (err) {
          console.error(`Invalid props value "${props}" for ${key} component. Must be a json string.`, el);
        }
      }

      try {
        // Load async component
        if (typeof Component === 'function' && !Component.isComponent) {
          Component = await Component().then((m) => m.default || m);
        }

        // Initialize component instance
        const instance = new Component(el, props, key, this);
        this.$componentsInstances.set(el, instance);
        instance.$init();
      } catch (err) {
        if (/^Loading( CSS)? chunk (\d)+ failed\./.test(err.message)) {
          window.location.reload(true);
          return;
        }
        console.error(err);
      }
    });
  }

  $destroyComponents(el) {
    const elements = getMatchedElements(el, `[${COMPONENT_ATTR}]`);

    elements.forEach((el) => {
      const instance = this.$componentsInstances.get(el);

      if (instance) {
        instance.$destroy();
      }

      this.$componentsInstances.delete(el);
    });
  }

  $getComponentInstance(el) {
    return this.$componentsInstances.get(el);
  }

  $bindDirectives(el) {
    this.$loopElementsWithDirectives(el, (el, key, attr) => {
      const bound = this.$getBoundDirectives(el) || [];

      if (bound.includes(key)) return;

      let options = el.getAttribute(attr);

      if (options) {
        try {
          options = JSON.parse(options);
        } catch (err) {
          console.error(`Invalid options value "${options}" for ${key} directive. Must be a json string.`, el);
        }
      }

      try {
        this.$options.directives[key].bind(el, options);
        this.$boundDirectives.set(el, [...bound, key]);
      } catch (err) {
        console.error(err);
      }
    });
  }

  $unbindDirectives(el) {
    this.$loopElementsWithDirectives(el, (el, key) => {
      const bound = this.$getBoundDirectives(el);

      if (!bound || !bound.includes(key)) return;

      this.$options.directives[key].unbind(el);
      this.$boundDirectives.delete(el);
    });
  }

  $loopElementsWithDirectives(el, cb) {
    Object.keys(this.$options.directives).forEach((key) => {
      const attr = `${DIRECTIVE_ATTR}-${_kebabCase(key)}`;
      const elements = getMatchedElements(el, `[${attr}]`);
      elements.forEach((el) => cb(el, key, attr));
    });
  }

  $getBoundDirectives(el) {
    return this.$boundDirectives.get(el);
  }

  $initObserver() {
    if (!window.MutationObserver) {
      console.warn('Current browser doesn\'t support MutationObserver. You should call App.$initComponents() and App.$destroyComponents() manually on each DOM change.');
      return;
    }

    if (this.$observer) return;

    this.$observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const isNodeObservable = (node) => node.nodeType === Node.ELEMENT_NODE;

          Array.prototype.forEach.call(mutation.removedNodes, (node) => {
            if (isNodeObservable(node)) {
              this.$destroyComponents(node);
              this.$unbindDirectives(node);
            }
          });

          Array.prototype.forEach.call(mutation.addedNodes, (node) => {
            if (isNodeObservable(node)) {
              this.$initComponents(node);
              this.$bindDirectives(node);
            }
          });
        }
      });
    });

    this.$observer.observe(this.$el, {
      childList: true,
      subtree: true,
    });
  }
}

export default App;
